import React, { useState } from 'react';
import styled from 'styled-components';
import heartbeatBadIcon from 'src/images/customIcons/heartbeat-bad.png';
import heartbeatGoodIcon from 'src/images/customIcons/heartbeat-good.png';
import { SessionContext } from 'src/Views/SessionContext';
import IIntersystemNamespace from 'src/ServerEntities/IIntersystemsNamespaces';
import { getIntersystemsNamespaces, getRhapsodyHeader } from '../ramService';
import { RolesContext } from 'src/Roles/RolesContext';
import IRhapsodyServer from 'src/ServerEntities/IRhapsodyServer';
import useCookie from 'src/CustomHooks/useCookie';

interface StatusIndicatorProps {
  status: string;
}

interface ProgressBarProps {
  percentage: number;
}

interface ExpandedProps {
  expanded: boolean;
}

interface ColorProps {
  color: string;
}

const Container = styled.div`
  width: 100%;
  border-radius: 25px;
  border: 5px solid #44A2D6;
  overflow: hidden;
  font-family: Arial, sans-serif;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  margin: 24px 15px;
`;

const Header = styled.div`
  background-color: #0277bd;
  color: white;
  padding: 10px;
  text-align: center;
  font-weight: bold;
  font-size: 18px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  grid-auto-rows: min-content;
  gap: 10px;
  padding: 10px;
  align-items: start;
`;

const Card = styled.div`
  background-color: #1e3a5f;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: fit-content;
`;

const NamespaceHeader = styled.div<ColorProps>`
  background-color: ${props => props.color};
  color: white;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
`;

const NamespaceName = styled.div`
  flex-grow: 1;
`;

const Minutes = styled.span`
  font-size: 14px;
  margin-left: 5px;
`;

const HeartbeatIcon = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;
  height: 28px;
  width: 28px;
  
  img {
    height: 100%;
    width: 100%;
  }
`;

const StatusIndicators = styled.div<ExpandedProps>`
  display: ${props => props.expanded ? 'none' : 'flex'};
  padding: 10px;
  gap: 8px;
  justify-content: center;
`;

const StatusDot = styled.div<ColorProps>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => {
    switch(props.color) {
      case 'green': return '#43a047';
      case 'orange': return '#f57c00';
      case 'red': return '#e53935';
      default: return 'transparent';
    }
  }};
`;

const ExpandedContent = styled.div<ExpandedProps>`
  padding: 10px;
  color: white;
  display: ${props => props.expanded ? 'block' : 'none'};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const DetailLabel = styled.div`
  font-size: 14px;
`;

const DetailValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressBarContainer = styled.div`
  width: 100px;
  height: 12px;
  background-color: #444;
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressBar = styled.div<ProgressBarProps>`
  height: 100%;
  background-color: ${props => {
    if (props.percentage > 90) return '#e53935';
    if (props.percentage > 60) return '#f57c00';
    return '#43a047';
  }};
  width: ${props => props.percentage}%;
`;

const StatusIndicator = styled.div<StatusIndicatorProps>`
  width: 60px;
  height: 20px;
  border-radius: 10px;
  background-color: ${props => {
    switch(props.status) {
      case 'green': return '#43a047';
      case 'orange': return '#f57c00';
      case 'red': return '#e53935';
      default: return 'transparent';
    }
  }};
`;

const Namespaces = () => {
  const { state, dispatch } = React.useContext(SessionContext);
  const [expandedCards, setExpandedCards] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedNamespace] = useCookie<string>('selectedNamespace', '');
  const [namespaces, setNamespaces] = useState<IIntersystemNamespace[]>([]);

  const selectedServer = state.selectedSever ?? 0;

  const setSelectedServer = (index: number) => {
      dispatch({ type: "setSelectedServer", payload: index });
  };
  const roleContext = React.useContext(RolesContext);
  const currentRole = roleContext.state.role;

  React.useEffect(() => {
      setLoading(true);
      getRhapsodyHeader(state.webToken, (serverData: IRhapsodyServer[]) => {
        dispatch({ type: "setHeaderInfo", payload: serverData });

        const serverId = serverData[selectedServer]?.id ?? 0;
        getIntersystemsNamespaces(state.webToken, serverId, (data: IIntersystemNamespace[]) => {
          setLoading(false);
          setNamespaces(data);
          setError("");
        }, (errorMessage: string) => {
          setLoading(false);
          setError(errorMessage);
        });
      }, (errorMessage: string) => {
        setLoading(false);
        setError(errorMessage);
      }, selectedNamespace && selectedNamespace.trim() !== '' ? selectedNamespace : undefined);
  }, [currentRole, selectedNamespace, selectedServer]);

  React.useEffect(() => {
        if (state.selectedSever !== undefined) {
            setSelectedServer(state.selectedSever);
        }
  }, [state.selectedSever]);

  const handleCardClick = (index: number) => {
    setExpandedCards(prevExpanded => {
      if (prevExpanded.includes(index)) {
        return prevExpanded.filter(cardIndex => cardIndex !== index);
      } else {
        return [...prevExpanded, index];
      }
    });
  };

  const handleHeartbeatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const getStatusColor = (value: number | string, type: string): string => {
    if (type === 'alerts' || type === 'thresholdsInBreach' || type === 'currentTimeouts') {
      return Number(value) > 0 ? 'red' : 'green';
    }
    if (type === 'lastMsg') {
      return String(value).includes('m') ? 'red' : 'green';
    }
    return 'green';
  };

  const calculateMinutesSinceLastHeartbeat = (timestamp: number): number => {
    if (!timestamp) return 0;
    
    try {
      const heartbeatDate = new Date(timestamp);
      const currentDate = new Date();
      const diffMs = currentDate.getTime() - heartbeatDate.getTime();
      return Math.floor(diffMs / 60000);
    } catch (e) {
      console.error("Error calculating minutes since last heartbeat:", e);
      return 0;
    }
  };

  const getNamespaceColor = (namespace: IIntersystemNamespace): string => {
    if (namespace.alertCount > 3) return '#e53935';
    if (namespace.alertCount > 0) return '#f57c00';
    return '#43a047';
  };

  const isHeartbeatHealthy = (timestamp: number): boolean => {
    return calculateMinutesSinceLastHeartbeat(timestamp) < 5;
  };

  const formatDatabaseSize = (sizeGb: number): string => {
    return `${sizeGb.toFixed(2)}GB`;
  };

  const getLastMsgTime = (timestamp: number): string => {
    const minutes = calculateMinutesSinceLastHeartbeat(timestamp);
    if (minutes > 0) {
      return `${minutes}m${Math.floor(Math.random() * 60)}s`;
    }
    return `${Math.floor(Math.random() * 60)}s`;
  };

  const generateStatusDots = (namespace: IIntersystemNamespace): string[] => {
    const dots: string[] = [];
    
    dots.push(namespace.alertCount > 0 ? 'red' : 'green');
    
    dots.push(namespace.thresholdsInBreach > 0 ? 'orange' : 'green');
    
    dots.push(namespace.currentTimeouts > 0 ? 'red' : 'green');
    
    if (namespace.databaseUsedPercentage > 90) {
      dots.push('red');
    } else if (namespace.databaseUsedPercentage > 60) {
      dots.push('orange');
    } else {
      dots.push('green');
    }
    
    dots.push(isHeartbeatHealthy(namespace.lastHeartbeatTimestamp) ? 'green' : 'red');
    
    return dots;
  };

  if (loading) {
    return <Container><Header>Loading namespaces...</Header></Container>;
  }

  if (error) {
    return <Container><Header>Error: {error}</Header></Container>;
  }

  return (
    <Container>
      <Header>Namespaces & Heartbeats</Header>
      <Grid>
        {namespaces.map((namespace, index) => {
          const isExpanded = expandedCards.includes(index);
          const minutes = calculateMinutesSinceLastHeartbeat(namespace.lastHeartbeatTimestamp);
          const color = getNamespaceColor(namespace);
          const heartbeatHealthy = isHeartbeatHealthy(namespace.lastHeartbeatTimestamp);
          const statusDots = generateStatusDots(namespace);
          const lastMsg = getLastMsgTime(namespace.lastHeartbeatTimestamp);
          
          return (
            <Card key={namespace.id} onClick={() => handleCardClick(index)}>
              <NamespaceHeader color={color}>
                <NamespaceName>
                  {namespace.namespace || `Namespace ${namespace.id}`}
                  <Minutes>{minutes} minutes</Minutes>
                </NamespaceName>
                <HeartbeatIcon onClick={handleHeartbeatClick}>
                  <img 
                    height={28} 
                    width={28} 
                    src={heartbeatHealthy ? heartbeatGoodIcon : heartbeatBadIcon} 
                    alt={heartbeatHealthy ? "Healthy heartbeat" : "Unhealthy heartbeat"}
                  />
                </HeartbeatIcon>
              </NamespaceHeader>
            
              <StatusIndicators expanded={isExpanded}>
                {statusDots.map((color, statusIndex) => (
                  <StatusDot key={statusIndex} color={color} />
                ))}
              </StatusIndicators>
              
              <ExpandedContent expanded={isExpanded}>
                <DetailRow>
                  <DetailLabel>Alerts</DetailLabel>
                  <DetailValue>
                    {namespace.alertCount}
                    <StatusIndicator status={getStatusColor(namespace.alertCount, 'alerts')} />
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Thresholds in breach</DetailLabel>
                  <DetailValue>
                    {namespace.thresholdsInBreach}
                    <StatusIndicator status={getStatusColor(namespace.thresholdsInBreach, 'thresholdsInBreach')} />
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Current Timeouts</DetailLabel>
                  <DetailValue>
                    {namespace.currentTimeouts}
                    <StatusIndicator status={getStatusColor(namespace.currentTimeouts, 'currentTimeouts')} />
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Last Msg</DetailLabel>
                  <DetailValue>
                    {lastMsg}
                    <StatusIndicator status={getStatusColor(lastMsg, 'lastMsg')} />
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Database Size</DetailLabel>
                  <DetailValue>
                    {formatDatabaseSize(namespace.databaseTotalSizeGb)}
                    <ProgressBarContainer>
                      <ProgressBar percentage={namespace.databaseUsedPercentage} />
                    </ProgressBarContainer>
                  </DetailValue>
                </DetailRow>
              </ExpandedContent>
            </Card>
          );
        })}
      </Grid>
    </Container>
  );
};

export default Namespaces;
